import { memo, useEffect } from 'react';
import { useAntdTable } from 'ahooks';
import { Data } from 'ahooks/lib/useAntdTable/types';
import { Params } from 'ahooks/lib/usePagination/types';
import { Form, FormProps } from 'antd';
import { Store } from 'antd/es/form/interface';
import { Gutter } from 'antd/lib/grid/row';
import clsx from 'clsx';
import { debounce } from 'lodash';

import { Container, IMilesFormProps, MilesForm, MilesTable } from 'components';

import { checkSessionAction, commonStore } from 'stores/common';

import { IMilesTableProps, TFieldProps } from 'types';

const { useForm } = Form;

interface ITableWithForm<T> extends Omit<IMilesTableProps<T>, 'id'> {
  id: string;
  fields?: TFieldProps[];
  gutter?: Gutter | [Gutter, Gutter];
  formInitialValues?: Store;
  defaultTableParams?: Params[0];
  resetButtonProps?: IMilesFormProps<T>['buttonProps'];
  withResetButton?: boolean;
  onTableChange: (...args: Params) => Promise<Data>;
}

const TableWithForm = <T extends Record<string, unknown>, >({
  id,
  size,
  rowKey = 'id',
  fields,
  gutter,
  layout,
  columns,
  labelCol,
  wrapperCol,
  sortDirections,
  titleExtraNode,
  withResetButton,
  resetButtonProps,
  horizontalScroll,
  formInitialValues,
  defaultTableParams,
  expandable,
  onTableChange,
}: ITableWithForm<T> & Omit<FormProps, 'fields'>) => {
  const user = commonStore((state) => state.user);
  const isSessionExpired = commonStore((state) => state.isSessionExpired);

  const [form] = useForm();

  const { tableProps, search: { submit, reset } } = useAntdTable(onTableChange, {
    form,
    ready: !!user && !isSessionExpired,
    defaultParams: [defaultTableParams || { current: 1, pageSize: 50 }, formInitialValues],
  });

  useEffect(() => {
    checkSessionAction();
  }, [tableProps.loading]);

  const preparedFields: TFieldProps[] | undefined = fields?.map((field) => {
    switch (field.type) {
      case TFieldProps.type.Select:
        return {
          ...field,
          onSelectChange: submit,
        };
      case TFieldProps.type.DateRangePicker:
        return {
          ...field,
          onDateRangeChange: submit,
        };
      case TFieldProps.type.DatePicker:
        return {
          ...field,
          onDateChange: submit,
        };
      case TFieldProps.type.Checkbox:
        return {
          ...field,
          onCheckboxChange: submit,
        };
      case TFieldProps.type.Text:
        return {
          ...field,
          onTextChange: debounce(submit, 700),
        };
      default:
        return field;
    }
  });

  const resetBtn = (resetButtonProps || withResetButton) ? {
    ...resetButtonProps,
    label: 'Reset',
    loading: tableProps.loading,
    onClick: reset,
  } : undefined;

  return (
    <>
      <Container className={clsx('flex-initial pb-0', !preparedFields && 'hidden')}>
        <MilesForm
          name={id}
          form={form}
          fields={preparedFields || []}
          gutter={gutter || [10, 10]}
          layout={layout}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          buttonProps={resetBtn}
        />
      </Container>

      <Container>
        <MilesTable
          verticalScroll
          id={id}
          size={size}
          rowKey={rowKey}
          columns={columns}
          loading={tableProps.loading}
          expandable={expandable}
          pagination={{ ...tableProps.pagination, size: 'default' }}
          dataSource={tableProps.dataSource}
          sortDirections={sortDirections}
          titleExtraNode={titleExtraNode}
          horizontalScroll={horizontalScroll}
          onChange={tableProps.onChange}
        />
      </Container>
    </>
  );
};

export default memo(TableWithForm);
